import type { ContentCard } from '@braze/react-native-sdk'
import { DesignSystem } from '..'
import { useCallback, useEffect } from 'react'
import * as cardMetrics from '@/src/services/Braze/contentCardMetrics'
import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import { useMediaQuery } from 'react-responsive'
import { TRANSITION_TO_SPLIT_SCREEN_WIDTH } from './types'
import { View, type ViewProps, Text, StyleSheet } from 'react-native'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { Image } from 'expo-image'
import { useLogError } from '@/src/common/hooks/useLogError'
import { navigateToAppUrl } from './navigateToAppUrl'
import { typography } from '@/src/common/theme'

const styles = StyleSheet.create({
  card: { flex: 1 },
  cardHeader: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingVertical: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardContent: {
    flexDirection: 'row',
    flexGrow: 0.5,
  },
  title: typography['heading/heading3'],
  description: {
    flexGrow: 1,
    ...typography['paragraph/sm'],
  },
  textContainer: {
    flexDirection: 'column',
    flex: 1,
    gap: 8,
  },
  chevronContainer: {
    marginLeft: 8,
    alignSelf: 'center',
  },
  image: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
})

type CaptionedCard = ContentCard & {
  type: 'Captioned'
}

export default function CaptionedCard({
  contentCard,
  minWidth,
  minHeight,
  style,
}: {
  contentCard: CaptionedCard
  minWidth?: number | string
  minHeight?: number | string
  style?: ViewProps['style']
}) {
  const logError = useLogError()
  const isSplitScreen = useMediaQuery({ minWidth: TRANSITION_TO_SPLIT_SCREEN_WIDTH })

  useEffect(() => {
    cardMetrics.logImpression(contentCard)
  }, [contentCard])

  const { title, cardDescription, url, openURLInWebView, image } = contentCard
  const { blurhash, thumbhash } = contentCard.extras

  const pressHandler = useCallback(() => {
    if (!url) {
      return
    }

    cardMetrics.logClick(contentCard)

    if (openURLInWebView) {
      void openInAppBrowser(url, title, 'tab')
    } else {
      navigateToAppUrl({ navigationUrl: url, onError: logError })
    }
  }, [url, title, openURLInWebView, contentCard, logError])

  if (contentCard.isControl) {
    // Do not render control cards, log impressions only.
    return null
  }

  return (
    <DesignSystem.SplitCard
      onPress={url ? pressHandler : undefined}
      style={[styles.card, { minWidth, minHeight }, style]}
    >
      <DesignSystem.SplitCard.Header
        containerStyle={[
          styles.cardHeader,
          {
            aspectRatio:
              contentCard.imageAspectRatio > 0 ? contentCard.imageAspectRatio : undefined,
          },
          isSplitScreen && { minHeight: 200 },
        ]}
      >
        <Image
          source={{ uri: image }}
          contentFit="cover"
          style={styles.image}
          placeholder={{ blurhash, thumbhash }}
          transition={500}
        />
      </DesignSystem.SplitCard.Header>
      <DesignSystem.SplitCard.Content style={styles.cardContent}>
        <View style={styles.textContainer}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.description}>{cardDescription}</Text>
        </View>
        {url && (
          <View style={styles.chevronContainer}>
            <ChevronRight />
          </View>
        )}
      </DesignSystem.SplitCard.Content>
    </DesignSystem.SplitCard>
  )
}
